import { forwardRef, useCallback, useRef, useState } from 'react';
import { DisclosureExpand } from '@sas/react-svg';
import cx from 'classnames';
import { useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { ButtonVariant } from '../../Button';
import { BaseButton } from '../../Button/__internal__';
import { Icon } from '../../Icon';
import { BaseMenu, getNextFocusableLocation } from '../../Menu/__internal__';
import { useToggleMenu } from '../useToggleMenu';
import '../../Button/__internal__/BaseButton.css';
import cnBaseButton from '../../Button/__internal__/BaseButton.css.json';
import './BaseMenuButton.css';
import cn from './BaseMenuButton.css.json';

/**
 * @internal
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const BaseMenuButton = /*#__PURE__*/forwardRef(function BaseMenuButton({
  children,
  icon,
  onItemSelected,
  onItemClosed,
  onDismiss,
  onOpen,
  onMouseDown,
  onMouseUp,
  onKeyDown,
  onKeyUp,
  parentWidth,
  renderButton,
  sections,
  hideDownArrow,
  menuAlign,
  menuDockingElement,
  testId,
  buttonTheme,
  listTheme,
  menuTheme,
  popoverTheme,
  ...props
}, ref) {
  const [defaultFocusLocation, setDefaultFocusLocation] = useState();
  const buttonRef = useRef(null);
  const {
    handleDismiss,
    handleMouseDown,
    handlePress,
    setMenuIsOpen,
    menuIsOpen
  } = useToggleMenu({
    disabled: props.disabled,
    onDismiss: () => {
      buttonRef.current?.focus();
      onDismiss?.();
    },
    onOpen: () => {
      onOpen?.();
      setDefaultFocusLocation(undefined);
    }
  });
  const handleItemSelected = (indices, key) => {
    buttonRef.current?.focus();
    onItemSelected?.(indices, key);
    setMenuIsOpen(false);
    onDismiss?.();
  };
  const setButtonRef = useCallback(button => {
    buttonRef.current = button;
    setRef(ref, button);
  }, [ref]);
  const isButtonKeyDown = useRef(false);
  const handleKeyUp = useCallback(e => {
    if (props.disabled) return;
    if (!isButtonKeyDown.current) {
      return;
    }
    isButtonKeyDown.current = false;
    onKeyUp?.(e);
    let focusLocation;
    const hasModifier = e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;
    if ((e.key === 'Enter' || e.key === ' ') && !hasModifier || e.altKey && e.key === 'ArrowDown') {
      setMenuIsOpen(true);
      onOpen?.();
      focusLocation = sections.length > 0 ? getNextFocusableLocation(-1, sections, false) : undefined;
    } else if (e.altKey && e.key === 'ArrowUp') {
      setMenuIsOpen(true);
      onOpen?.();
      focusLocation = sections.length > 0 ? getNextFocusableLocation(0, sections, true) : undefined;
    }
    if (focusLocation !== undefined) {
      e.preventDefault();
      setDefaultFocusLocation(focusLocation);
      // prevents further propagation of the current event in the capturing and bubbling phases.
      // it fixes the issue "focus move to another example in Storybook"
      e.stopPropagation();
    }
  }, [onKeyUp, onOpen, props.disabled, sections, setMenuIsOpen]);
  const dt = useDesignTokens();
  const buttonProps = {
    ...props,
    theme: buttonTheme,
    icon,
    children: hideDownArrow ?? !!icon ? null : /*#__PURE__*/_jsx(Icon, {
      icon: DisclosureExpand,
      className: cx(cnBaseButton.iconInner, cn.icon),
      tooltip: "",
      ariaHidden: true
    }),
    onMouseDown: e => {
      handleMouseDown(e);
      onMouseDown?.(e);
    },
    onMouseUp,
    onClick: handlePress,
    onKeyDown: e => {
      if (isButtonKeyDown.current) {
        return;
      }
      isButtonKeyDown.current = true;
      if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.stopPropagation();
      }
      onKeyDown?.(e);
    },
    onKeyUp: e => {
      handleKeyUp(e);
    },
    onBlur: () => {
      isButtonKeyDown.current = false;
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": testId,
    className: props.className ? `${props.className}-container` : undefined,
    children: [renderButton ? renderButton({
      ...buttonProps,
      menuIsOpen,
      setButtonRef
    }) : /*#__PURE__*/_jsx(BaseButton, {
      tokens: () => getMenuButtonTokens(menuIsOpen, buttonProps.variant, props.text, dt),
      ...buttonProps,
      icon: buttonProps.icon ? {
        ...buttonProps.icon,
        theme: menuIsOpen ? () => {
          if (dt.themeVariant === 'high-contrast') {
            return {
              '--icon-default-override-fg': dt.tokens['color-action-icon-emphasis-selected'],
              '--icon-success-override-fg': dt.tokens['color-action-icon-emphasis-selected'],
              '--icon-alert-override-fg': dt.tokens['color-action-icon-emphasis-selected'],
              '--icon-warning-override-fg': dt.tokens['color-action-icon-emphasis-selected'],
              '--icon-information-override-fg': dt.tokens['color-action-icon-emphasis-selected'],
              '--icon-neutral-override-fg': dt.tokens['color-action-icon-emphasis-selected']
            };
          } else if (buttonProps.variant === ButtonVariant.PrimaryIcon) {
            switch (dt.themeVariant) {
              case 'dark':
                return {
                  '--icon-default-override-fg': dt.tokens['color-icon-primary'],
                  '--icon-success-override-fg': dt.tokens['color-icon-primary'],
                  '--icon-alert-override-fg': dt.tokens['color-icon-primary'],
                  '--icon-warning-override-fg': dt.tokens['color-icon-primary'],
                  '--icon-information-override-fg': dt.tokens['color-icon-primary'],
                  '--icon-neutral-override-fg': dt.tokens['color-icon-primary']
                };
              default:
                return {
                  '--icon-default-override-fg': dt.tokens['color-action-icon-emphasis-normal'],
                  '--icon-success-override-fg': dt.tokens['color-action-icon-emphasis-normal'],
                  '--icon-alert-override-fg': dt.tokens['color-action-icon-emphasis-normal'],
                  '--icon-warning-override-fg': dt.tokens['color-action-icon-emphasis-normal'],
                  '--icon-information-override-fg': dt.tokens['color-action-icon-emphasis-normal'],
                  '--icon-neutral-override-fg': dt.tokens['color-action-icon-emphasis-normal']
                };
            }
          } else {
            return undefined;
          }
        } : undefined
      } : undefined,
      ref: setButtonRef,
      testId: postfixTestId(testId, 'button'),
      "aria-haspopup": true,
      "aria-expanded": menuIsOpen
    }), /*#__PURE__*/_jsx(BaseMenu, {
      testId: postfixTestId(testId, 'menu'),
      isOpen: menuIsOpen,
      sections: sections,
      position: {
        dockingElement: menuDockingElement ?? buttonRef.current,
        ...menuAlign
      },
      onItemSelected: handleItemSelected,
      onItemClosed: onItemClosed,
      onDismiss: handleDismiss,
      defaultFocusLocation: defaultFocusLocation,
      theme: menuTheme,
      listTheme: listTheme,
      popoverTheme: popoverTheme
    })]
  });
});

/**
 * @internal
 */
export const getMenuButtonTokens = (menuIsOpen, variant, text, dt) => {
  const defaultTokens = menuIsOpen && variant !== ButtonVariant.PrimaryLight ? {
    fg: dt.tokens['color-action-text-standard-selected'],
    bg: dt.tokens['color-action-background-standard-selected'],
    bd: text ? dt.tokens['color-action-border-standard-selected'] : 'transparent'
  } : {};
  switch (dt.themeVariant) {
    case 'dark':
    case 'high-contrast':
      return menuIsOpen && (variant === ButtonVariant.Primary || variant === ButtonVariant.PrimaryIcon || variant === ButtonVariant.PrimarySmall || variant === ButtonVariant.PrimaryLight) ? {
        bg: dt.tokens['color-action-background-emphasis-selected'],
        bd: dt.tokens['color-action-border-emphasis-selected'],
        fg: dt.tokens['color-action-text-emphasis-selected']
      } : defaultTokens;
    default:
      return menuIsOpen && (variant === ButtonVariant.Primary || variant === ButtonVariant.PrimaryIcon || variant === ButtonVariant.PrimarySmall || variant === ButtonVariant.PrimaryLight) ? {
        bg: dt.tokens['color-action-background-emphasis-selected'],
        bd: 'transparent',
        fg: dt.tokens['color-text-primary-inverse']
      } : defaultTokens;
  }
};
//# sourceMappingURL=BaseMenuButton.js.map